import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import AppHeader from "../components/AppHeader";

const STATUSES = [
  { key: "sin_empezar", label: "Sin empezar" },
  { key: "en_progreso", label: "En progreso" },
  { key: "preparado_subasta", label: "Preparado para subasta" },
  { key: "pendiente_gestion", label: "Pendiente de gestión" },
  { key: "terminado", label: "Terminado" },
];
const PRIORITIES = [{ key: "baja", label: "Baja" }, { key: "media", label: "Media" }, { key: "alta", label: "Alta" }];

const fromDtLocal = (v) => v ? new Date(v).toISOString() : null;

export default function CRMCardNew() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    title: "",
    client_name: "",
    client_email: "",
    client_phone: "",
    auction_id: "",
    status: "sin_empezar",
    priority: "media",
    entry_date: new Date().toISOString().slice(0, 10),
    auction_date_local: "",
    next_contact_local: "",
    tags: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.title.trim() || f.title.trim().length < 2) { setError("Indica un título."); return; }
    setSaving(true);
    try {
      const payload = {
        title: f.title.trim(),
        client_name: f.client_name.trim(),
        client_email: f.client_email.trim(),
        client_phone: f.client_phone.trim(),
        auction_id: f.auction_id.trim(),
        status: f.status,
        priority: f.priority,
        entry_date: f.entry_date || null,
        auction_date: fromDtLocal(f.auction_date_local),
        next_contact: fromDtLocal(f.next_contact_local),
        tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
        description: f.description.trim(),
      };
      const { data } = await api.post("/admin/crm/cards", payload);
      navigate(`/admin/crm/${data.id}`);
    } catch (e2) {
      setError(e2?.response?.data?.detail || "No se pudo crear");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <AppHeader />
      <div className="border-b border-stone-800 bg-stone-900 px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <Link to="/admin/crm" className="text-stone-400 hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold">
            <ArrowLeft className="w-4 h-4" /> Volver al tablero
          </Link>
        </div>
      </div>
      <main className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="font-display text-4xl font-black tracking-tight text-white mb-8">Nueva tarjeta</h1>
        {error && (
          <div className="bg-red-900/50 text-red-200 border border-red-700 px-4 py-2 text-sm rounded-sm mb-4 inline-flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>
        )}
        <form onSubmit={submit} className="space-y-5 bg-stone-900 border border-stone-800 p-6 rounded-sm" data-testid="crm-new-form">
          <Field label="Título *">
            <input value={f.title} onChange={upd("title")} className="field" required data-testid="new-title" autoFocus />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre cliente"><input value={f.client_name} onChange={upd("client_name")} className="field" data-testid="new-client-name" /></Field>
            <Field label="Email"><input type="email" value={f.client_email} onChange={upd("client_email")} className="field" data-testid="new-client-email" /></Field>
            <Field label="Teléfono"><input value={f.client_phone} onChange={upd("client_phone")} className="field" data-testid="new-client-phone" /></Field>
            <Field label="ID Subasta (BOE)"><input value={f.auction_id} onChange={upd("auction_id")} className="field font-mono" data-testid="new-auction-id" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Estado">
              <select value={f.status} onChange={upd("status")} className="field" data-testid="new-status">
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Prioridad">
              <select value={f.priority} onChange={upd("priority")} className="field" data-testid="new-priority">
                {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </Field>
            <Field label="Fecha entrada"><input type="date" value={f.entry_date} onChange={upd("entry_date")} className="field" /></Field>
            <Field label="Fecha subasta (con hora)"><input type="datetime-local" value={f.auction_date_local} onChange={upd("auction_date_local")} className="field" data-testid="new-auction-date" /></Field>
            <Field label="Próximo contacto"><input type="datetime-local" value={f.next_contact_local} onChange={upd("next_contact_local")} className="field" /></Field>
            <Field label="Etiquetas (separadas por comas)"><input value={f.tags} onChange={upd("tags")} className="field" placeholder="Consultoría, Subasta..." /></Field>
          </div>
          <Field label="Descripción / Notas">
            <textarea value={f.description} onChange={upd("description")} rows={4} className="field resize-none" />
          </Field>
          <button type="submit" disabled={saving}
            className="bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-60 text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm inline-flex items-center gap-2" data-testid="new-submit">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creando…</> : <><Save className="w-4 h-4" /> Crear tarjeta</>}
          </button>
        </form>
      </main>
      <style>{`
        .field { width:100%; background:#1c1917; border:1px solid #44403c; color:#f5f5f4; padding:0.55rem 0.75rem; font-size:0.9rem; border-radius:2px; outline:none; transition:border-color .15s; }
        .field:focus { border-color:#E0651F; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
